// src/components/pos/ShoppingCart.jsx - 整体等高,内部滚动版本
import React, { useRef, useEffect, useState } from 'react';
import { Trash2, Plus, Minus } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { translations } from '../../utils/translations';

const ShoppingCart = ({ 
  cartItems = [],
  onUpdateQuantity, 
  onRemoveItem,
  onCheckout,
  paymentMethod = 'cash',
  onPaymentMethodChange
}) => {
  const { language } = useApp();
  const t = translations[language];
  const containerRef = useRef(null);
  const [cartListHeight, setCartListHeight] = useState(300);

  // 获取商品显示名称
  const getDisplayName = (item) => {
    switch (language) {
      case 'en':
        return item.product_name_en || item.product_name;
      case 'km':
        return item.product_name_km || item.product_name;
      default:
        return item.product_name;
    }
  };

  // 计算购物车列表的可用高度
  useEffect(() => {
    const calculateHeight = () => {
      if (containerRef.current) {
        const container = containerRef.current;
        const containerHeight = container.offsetHeight;
        
        // 减去固定部分的高度
        // 标题(约60px) + 统计(约80px) + 支付方式(约180px) + 结算按钮(约60px) + padding
        const fixedHeight = 60 + 80 + 180 + 60 + 40; // 总共约420px
        const availableHeight = containerHeight - fixedHeight;
        
        setCartListHeight(Math.max(availableHeight, 200)); // 最小200px
      }
    };

    calculateHeight();
    window.addEventListener('resize', calculateHeight);
    
    return () => window.removeEventListener('resize', calculateHeight);
  }, []);

  // 计算总额
  const totalAmount = Array.isArray(cartItems) 
    ? cartItems.reduce((sum, item) => sum + (item.unit_price || 0) * (item.quantity || 0), 0)
    : 0;

  const totalItems = Array.isArray(cartItems)
    ? cartItems.reduce((sum, item) => sum + (item.quantity || 0), 0)
    : 0;

  return (
    <div 
      ref={containerRef}
      className="bg-white rounded-xl shadow-lg p-6 h-full flex flex-col"
    >
      {/* 标题 - 固定 */}
      <h3 className="text-xl font-bold mb-4">{t.cart}</h3>
      
      {/* 购物车列表 - 可滚动区域,高度动态计算 */}
      <div 
        className="overflow-y-auto mb-4"
        style={{ height: `${cartListHeight}px` }}
      >
        {!cartItems || cartItems.length === 0 ? (
          <div className="text-center text-gray-400 py-12">
            <p>{t.cartIsEmpty || '购物车是空的'}</p>
            <p className="text-sm mt-2">
              {language === 'en' ? 'Please select products to add' :
               language === 'km' ? 'សូមជ្រើសរើសទំនិញដើម្បីបន្ថែម' :
               '请选择商品添加'}
            </p>
          </div>
        ) : (
          <div className="space-y-3 pr-2">
            {cartItems.map((item, index) => (
              <div 
                key={item.product_id || index} 
                className="border rounded-lg p-3 hover:shadow-md transition-shadow"
              >
                <div className="flex justify-between items-start mb-2">
                  <div className="flex-1">
                    <h4 className="font-medium text-gray-800 text-sm">
                      {getDisplayName(item)}
                    </h4>
                    <p className="text-xs text-gray-500">
                      ¥{(item.unit_price || 0).toFixed(2)} × {item.quantity}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-blue-600 text-sm">
                      ¥{((item.unit_price || 0) * item.quantity).toFixed(2)}
                    </p>
                  </div>
                </div>
                
                <div className="flex justify-between items-center">
                  {/* 数量控制 */}
                  <div className="flex items-center space-x-1">
                    <button
                      onClick={() => onUpdateQuantity && onUpdateQuantity(item.product_id, item.quantity - 1)}
                      className="p-1 rounded bg-gray-100 hover:bg-gray-200 transition-colors"
                      title={t.decrease || '减少'}
                    >
                      <Minus className="w-3 h-3" />
                    </button>
                    <span className="w-10 text-center font-medium text-sm">
                      {item.quantity}
                    </span>
                    <button
                      onClick={() => onUpdateQuantity && onUpdateQuantity(item.product_id, item.quantity + 1)}
                      className="p-1 rounded bg-gray-100 hover:bg-gray-200 transition-colors"
                      title={t.increase || '增加'}
                    >
                      <Plus className="w-3 h-3" />
                    </button>
                  </div>
                  
                  {/* 删除按钮 */}
                  <button
                    onClick={() => onRemoveItem && onRemoveItem(item.product_id)}
                    className="text-red-500 hover:text-red-700 transition-colors"
                    title={t.delete || '删除'}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* 底部固定区域 */}
      <div className="border-t pt-4 space-y-4 mt-auto">
        {/* 统计信息 */}
        <div className="space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-gray-600">
              {language === 'en' ? 'Items' :
               language === 'km' ? 'ចំនួនទំនិញ' :
               '商品数量'}:
            </span>
            <span className="font-medium">
              {totalItems} {language === 'en' ? 'pcs' :
                           language === 'km' ? 'ក្បាល' :
                           '件'}
            </span>
          </div>
          
          <div className="flex justify-between items-center text-lg font-bold">
            <span>{t.total}:</span>
            <span className="text-blue-600">¥{totalAmount.toFixed(2)}</span>
          </div>
        </div>

        {/* 支付方式选择 */}
        <div className="border-t pt-3">
          <h4 className="font-semibold mb-2 text-sm">{t.paymentMethod}</h4>
          <div className="space-y-1">
            <label className="flex items-center cursor-pointer hover:bg-gray-50 p-2 rounded transition-colors">
              <input
                type="radio"
                value="cash"
                checked={paymentMethod === 'cash'}
                onChange={(e) => onPaymentMethodChange && onPaymentMethodChange(e.target.value)}
                className="mr-2"
              />
              <span className="text-sm">{t.cash}</span>
            </label>
            <label className="flex items-center cursor-pointer hover:bg-gray-50 p-2 rounded transition-colors">
              <input
                type="radio"
                value="card"
                checked={paymentMethod === 'card'}
                onChange={(e) => onPaymentMethodChange && onPaymentMethodChange(e.target.value)}
                className="mr-2"
              />
              <span className="text-sm">{t.card}</span>
            </label>
            <label className="flex items-center cursor-pointer hover:bg-gray-50 p-2 rounded transition-colors">
              <input
                type="radio"
                value="mobile"
                checked={paymentMethod === 'mobile'}
                onChange={(e) => onPaymentMethodChange && onPaymentMethodChange(e.target.value)}
                className="mr-2"
              />
              <span className="text-sm">{t.mobile}</span>
            </label>
          </div>
        </div>

        {/* 结算按钮 */}
        <button
          onClick={onCheckout}
          disabled={!cartItems || cartItems.length === 0}
          className={`
            w-full py-3 rounded-lg font-semibold text-white transition-colors
            ${cartItems && cartItems.length > 0
              ? 'bg-blue-600 hover:bg-blue-700 cursor-pointer'
              : 'bg-gray-300 cursor-not-allowed'
            }
          `}
        >
          {cartItems && cartItems.length > 0 
            ? t.checkout 
            : (language === 'en' ? 'Add products first' :
               language === 'km' ? 'បន្ថែមទំនិញជាមុនសិន' :
               '请先添加商品')}
        </button>
      </div>
    </div>
  );
};

export default ShoppingCart;